import { useEffect, useRef, useState } from 'react';
import {
  useUploadCardMutation, useExtractCardMutation, useReviewCardMutation, useUpdateContactTypeMutation,
  useConfirmCardMutation, useCancelCardMutation, useCardQuery, useCrmTagsQuery,
  useIntakeSettingsQuery,
  type CardDetailResponse, type CardDraft, type CardReviewResult, type CrmTagVocabulary,
  type DraftField, type IntakeSettings, type PostalAddress,
} from './api.js';
import { CardBatch } from './CardBatch.js';
import { CrmTagPicker, EMPTY_VOCABULARY } from './CrmTagPicker.js';
import { ContactTypeField } from './ContactTypeField.js';
import { SharedAddressConfirm } from './SharedAddress.js';
import { contactTypeBlocks, contactTypeView, labelOf } from './contactType.js';
import { fromSelection, labelFor, sameSelection, stageLabel, toSelection, withdrawnNote } from './crmTagSelection.js';
import { PhoneListEditor } from './PhoneListEditor.js';
import { PlanSummary } from './PlanSummary.js';
import { approvalUsageText } from './usageModel.js';
import {
  DELIVERY_TEXT, PHONE_TYPE_LABEL, cardLevelWarnings, fromDraftPhones, fromReviewedPhones,
  invalidPhoneError, phonesChanged, serializePhones, type PhoneRow,
} from './phoneList.js';
import {
  DANGER, DrawerActions, DrawerFooter, DrawerSource, DrawerSurface, useDrawerDirty, useDrawerDone,
} from './ReviewDrawer.js';
import { Alert, Button, Card, SectionHeading, Spinner, formatWhen } from './ui.js';

/**
 * Business card capture.
 *
 * Four steps, in the order the work actually happens: choose a photo, read it, correct
 * the reading, approve what gets written. The last step is the only one that can change
 * anything, and it is the only one that asks.
 */

type Step = 'capture' | 'review' | 'confirm' | 'done';

const LOW_CONFIDENCE = 0.7;

interface FormState {
  givenName: string; familyName: string; jobTitle: string; company: string;
  email: string; website: string;
  /** Every number on the card, in the person's order; the first callable one is primary. */
  phones: PhoneRow[];
  /** The CRM tags chosen for this contact (crmTagSelection ids). */
  tagIds: string[];
  /** The CRM contact type a person chose for a new contact. Never read from the card. */
  contactType: string | null;
  /**
   * The person chose it here (or cleared it). Until they do, the server keeps the type
   * already saved on the card, or pre-fills the workspace default.
   */
  contactTypeChosen?: boolean;
  /** The address as the person left it; what Keap is sent is the server's tidy copy of it. */
  address: AddressForm;
}

type AddressForm = Record<keyof PostalAddress, string>;
const ADDRESS_KEYS = ['line1', 'line2', 'locality', 'region', 'postalCode', 'countryCode'] as const;
const emptyAddress: AddressForm = { line1: '', line2: '', locality: '', region: '', postalCode: '', countryCode: '' };
const addressForm = (a: PostalAddress | null | undefined): AddressForm =>
  Object.fromEntries(ADDRESS_KEYS.map((k) => [k, a?.[k] ?? ''])) as AddressForm;
/** Blank components are null and an all-blank address is no address: nothing is invented. */
const addressBody = (a: AddressForm): PostalAddress | null => {
  const out = Object.fromEntries(ADDRESS_KEYS.map((k) => [k, a[k].trim() || null])) as unknown as PostalAddress;
  return ADDRESS_KEYS.some((k) => out[k] !== null) ? out : null;
};
const ADDRESS_FIELDS: ReadonlyArray<{ key: keyof PostalAddress; label: string; wide?: boolean; autoComplete: string }> = [
  { key: 'line1', label: 'Street address', wide: true, autoComplete: 'address-line1' },
  { key: 'line2', label: 'Apt, suite or unit', wide: true, autoComplete: 'address-line2' },
  { key: 'locality', label: 'City', autoComplete: 'address-level2' },
  { key: 'region', label: 'State or region', autoComplete: 'address-level1' },
  { key: 'postalCode', label: 'Postal code', autoComplete: 'postal-code' },
  { key: 'countryCode', label: 'Country', autoComplete: 'country' },
];

const emptyForm: FormState = {
  givenName: '', familyName: '', jobTitle: '', company: '',
  email: '', website: '', phones: [], tagIds: [], contactType: null, address: emptyAddress,
};

const TEXT_FIELDS = ['givenName', 'familyName', 'jobTitle', 'company', 'email', 'website'] as const;
/** Whether the form holds edits the server has not seen: a field, a number, or a tag. */
const formChanged = (a: FormState, b: FormState): boolean =>
  TEXT_FIELDS.some((k) => a[k].trim() !== b[k].trim())
  || phonesChanged(a.phones, b.phones)
  || !sameSelection(a.tagIds, b.tagIds)
  || a.contactType !== b.contactType
  || ADDRESS_KEYS.some((k) => a.address[k].trim() !== b.address[k].trim());

/**
 * The image & file default tag (one, or none), as the tag a NEW card starts with.
 *
 * A default, not a reading: a model reading a card cannot know whether somebody is a lead,
 * and nothing it extracted is ever turned into a tag. It is the workspace's own choice,
 * shown before Continue so the person can remove it — and a removed default stays removed.
 */
const defaultTags = (s: IntakeSettings | undefined, vocab: CrmTagVocabulary): string[] => toSelection({
  commercialStage: s?.defaultCommercialStage ?? null,
  relationshipTypeKeys: [],
  crmTagKeys: s?.defaultCrmTagKeys ?? [],
}, vocab).slice(0, 1);

const formFromDraft = (d: CardDraft): FormState => ({
  givenName: d.givenName.value ?? '',
  familyName: d.familyName.value ?? '',
  jobTitle: d.jobTitle.value ?? '',
  company: d.company.value ?? '',
  email: d.email.value ?? '',
  // Every number the card carries — never just the first.
  phones: fromDraftPhones(d.phones, d.phone.value),
  website: d.website.value ?? '',
  // Never pre-filled from the extraction; a new card is given the workspace defaults.
  tagIds: [],
  // A card says who someone is, not what they are to you: never inferred.
  contactType: null,
  address: addressForm(d.address),
});

/**
 * One reviewable field.
 *
 * Quiet unless there is something to say. Every value on this form was read from the
 * card, so saying so on every field was noise that taught people to ignore the one note
 * that matters: a reading the model was unsure of. That one is still marked, with what was
 * printed; a value the person changed gets a subtle "Edited".
 */
function Field({
  label, value, onChange, draft, type = 'text', edited,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  draft?: DraftField;
  type?: string;
  edited: boolean;
}) {
  const uncertain = !edited && draft?.confidence !== null && draft?.confidence !== undefined
    && draft.confidence < LOW_CONFIDENCE && Boolean(draft.value);

  return (
    <label className="block min-w-0 text-sm">
      <span className="flex flex-wrap items-baseline gap-2">
        <span className="text-slate-700">{label}</span>
        {uncertain ? (
          <span className="text-xs font-medium text-amber-800">Check this — hard to read</span>
        ) : edited && draft?.value ? (
          <span className="text-xs text-slate-400">Edited</span>
        ) : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 min-h-11 w-full min-w-0 rounded-md border px-3 py-2 text-base sm:text-sm ${
          uncertain ? 'border-amber-300 bg-amber-50/40' : 'border-slate-300'
        }`}
      />
      {uncertain && draft?.verbatim ? (
        <span className="mt-1 block text-xs text-slate-500 [overflow-wrap:anywhere]">Printed on the card: “{draft.verbatim}”</span>
      ) : null}
    </label>
  );
}

/**
 * A card reopened from Review: what the person already decided, over what was read.
 *
 * `reviewed` holds their corrections and wins field by field; the CRM tags are THEIR
 * earlier choices (never the model's), so unlike a fresh reading they are restored.
 */
const formFromResume = (r: CardDetailResponse, vocab: CrmTagVocabulary): FormState => {
  const base = r.draft ? formFromDraft(r.draft) : emptyForm;
  const v = r.reviewed;
  const pick = (mine: string | null | undefined, read: string) => (v ? mine ?? '' : read);
  return {
    givenName: pick(v?.givenName, base.givenName),
    familyName: pick(v?.familyName, base.familyName),
    jobTitle: pick(v?.jobTitle, base.jobTitle),
    company: pick(v?.company, base.company),
    email: pick(v?.email, base.email),
    // Their list; a card reviewed before lists existed resumes as its one number, typed Other.
    phones: fromReviewedPhones(v) ?? base.phones,
    website: pick(v?.website, base.website),
    tagIds: toSelection({
      commercialStage: r.capture.classification ?? null,
      relationshipTypeKeys: r.relationshipTypes,
      crmTagKeys: r.crmTagKeys ?? [],
    }, vocab),
    contactType: v?.contactType ?? null,
    // Their correction when they reviewed it, including clearing it; otherwise what was read.
    address: v ? addressForm(v.address) : base.address,
  };
};

const MATCH_COPY: Record<string, string> = {
  existing_mapping: 'Already linked to a Keap contact',
  exact_email: 'Matches an existing Keap contact by email',
  exact_phone: 'Matches an existing Keap contact by phone',
  prior_correspondence: 'Known to us, not yet linked in Keap',
  probabilistic: 'Possible matches — review required',
  no_match: 'No existing contact found',
};

/** The kinds of refused approval that a fresh look at the same form answers. */
const REFRESHABLE = new Set(['plan_changed', 'plan_expired']);

export function BusinessCard({ onOpenCrm, resumeId, onViewInReview }: {
  onOpenCrm: () => void;
  /**
   * Reopen a card already captured, from Review. Starts at the check-the-reading step,
   * prefilled; Continue makes a NEW plan and Confirm binds to that one, so a plan made
   * before the person left is never the one that is saved.
   */
  resumeId?: string;
  /** Shown where a multi-photo batch finishes. */
  onViewInReview?: () => void;
}) {
  const resuming = Boolean(resumeId);
  const [step, setStep] = useState<Step>(resuming ? 'review' : 'capture');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [captureId, setCaptureId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CardDraft | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [result, setResult] = useState<CardReviewResult | null>(null);
  const [saved, setSaved] = useState<{ externalContactId: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The server's refusal of a number, shown beside the numbers. The input is kept as typed. */
  const [phoneError, setPhoneError] = useState<{ message: string; values: string[] } | null>(null);

  const [upload, uploadState] = useUploadCardMutation();
  const [extract, extractState] = useExtractCardMutation();
  const [review, reviewState] = useReviewCardMutation();
  const [updateType, updateTypeState] = useUpdateContactTypeMutation();
  /** A proposed Contact Type change for the matched Keap contact: the contact and value seen, the value chosen. */
  const [typeProposal, setTypeProposal] = useState<{ ext: string; from: string | null; to: string; requestId: string } | null>(null);
  const [confirm, confirmState] = useConfirmCardMutation();
  const [cancel] = useCancelCardMutation();
  const resumed = useCardQuery(resumeId ?? '', { skip: !resumeId });
  const tags = useCrmTagsQuery();
  const intake = useIntakeSettingsQuery(undefined, { skip: resuming });
  const vocab = tags.data ?? EMPTY_VOCABULARY;
  /** A CRM key from a plan, as the tag name a person reads. Never the key itself. */
  const tagName = (key: string): string => stageLabel(key, vocab)
    ?? vocab.tags.find((t) => t.key === key)?.label
    ?? labelFor(vocab.retired?.find((t) => t.key === key)?.id ?? '', vocab);
  const [discarded, setDiscarded] = useState(false);
  /** The form as the server last saw it, so closing with unsaved edits can ask first. */
  const [savedForm, setSavedForm] = useState<FormState>(emptyForm);
  const seed = (f: FormState) => { setForm(f); setSavedForm(f); };
  const done = useDrawerDone();
  useDrawerDirty(step === 'review' && formChanged(form, savedForm));
  /** A tag that is no longer available blocks Continue until it is removed. */
  const blocked = withdrawnNote(form.tagIds, vocab);
  /*
   * Seeded once per card, not on every refetch: the card query is refreshed by the very
   * mutations this form makes, and re-seeding would throw away what was just typed.
   */
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const r = resumed.data;
    if (!resumeId || !r || seededFor.current === resumeId) return;
    seededFor.current = resumeId;
    setCaptureId(resumeId);
    setDraft(r.draft);
    seed(formFromResume(r, vocab));
    setResult(null);
    setSaved(null);
    setDiscarded(false);
    setStep(r.capture.status === 'confirmed' ? 'done' : 'review');
    if (r.capture.status === 'confirmed') setSaved({ externalContactId: r.capture.externalContactId });
  }, [resumeId, resumed.data]);

  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  // The preview is a local object URL. It is revoked on replacement and on unmount, so
  // the browser is not left holding the image after the workflow ends.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  /**
   * Takes the selected file and CLEARS the input.
   *
   * A file input fires `change` only when its value differs, so without the reset,
   * discarding a photo and then picking the same one again does nothing at all — the
   * second selection is identical to the first. Cancelling the picker fires no event,
   * which is what makes cancellation a no-op rather than a special case.
   */
  const chooseFile = (input: HTMLInputElement) => {
    const f = input.files?.[0] ?? null;
    input.value = '';
    if (!f) return;
    setError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const reset = async () => {
    if (captureId && step !== 'done') { try { await cancel(captureId).unwrap(); } catch { /* best effort */ } }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setStep('capture'); setFile(null); setPreviewUrl(null); setCaptureId(null);
    setDraft(null); seed(emptyForm); setResult(null); setSaved(null); setError(null); setPhoneError(null);
  };

  const messageFrom = (e: unknown, fallback: string): string => {
    const data = (e as { data?: { message?: string; error?: string } }).data;
    return data?.message ?? fallback;
  };

  const onExtract = async () => {
    if (!file) return;
    setError(null);
    try {
      const up = await upload(file).unwrap();
      setCaptureId(up.capture.id);
      const ex = await extract(up.capture.id).unwrap();
      setDraft(ex.draft);
      seed({ ...formFromDraft(ex.draft), tagIds: defaultTags(intake.data, vocab) });
      setStep('review');
    } catch (e) {
      setError(messageFrom(e, 'The card could not be read. Try again, or use a clearer photo.'));
    }
  };

  const onReExtract = async () => {
    if (!captureId) return;
    setError(null);
    try {
      const ex = await extract(captureId).unwrap();
      setDraft(ex.draft);
      // The tags already chosen — the person's own, or the defaults — survive a fresh reading.
      const next = { ...formFromDraft(ex.draft), tagIds: form.tagIds };
      setForm(next);
      setSavedForm({ ...next, tagIds: savedForm.tagIds });
    } catch (e) {
      setError(messageFrom(e, 'The card could not be read again. The image may already have been discarded.'));
    }
  };

  const onContinue = () => reviewWith(form);

  /**
   * Sends the form for checking; the Contact Type choice on the confirm step re-sends it,
   * and so does a shared-address decision (confirm, skip, or choose the Keap contact).
   */
  const reviewWith = async (form: FormState, shared: { confirmSharedAddress?: boolean; keapContactId?: string } = {}): Promise<boolean> => {
    if (!captureId) return false;
    setError(null);
    setPhoneError(null);
    const chosen = fromSelection(form.tagIds, vocab);
    try {
      const res = await review({
        id: captureId,
        body: {
          givenName: form.givenName || null,
          familyName: form.familyName || null,
          jobTitle: form.jobTitle || null,
          company: form.company || null,
          email: form.email || null,
          // The whole list, in order, always — including empty, which means "no phones".
          phones: serializePhones(form.phones),
          website: form.website || null,
          address: addressBody(form.address),
          classification: chosen.commercialStage,
          // Always sent, including empty: a removed tag is a decision, and omitting the
          // field would mean "unchanged" rather than "none of them".
          relationshipTypes: chosen.relationshipTypeKeys,
          crmTagKeys: chosen.crmTagKeys,
          // Only a person's own choice is sent; otherwise the saved type or the workspace default stands.
          ...(form.contactTypeChosen ? { contactType: form.contactType } : {}),
          ...shared,
        },
      }).unwrap();
      setResult(res);
      /*
       * A proposal is of one contact and one value as seen. If Keap's value or the matched
       * contact moved since, it is dropped and the person is told, never carried over.
       */
      if (typeProposal) {
        const ids = res.identity.requiresReview ? [] : res.identity.externalContactIds;
        if (ids.length !== 1 || ids[0] !== typeProposal.ext || (res.existingContactType ?? null) !== typeProposal.from) {
          setTypeProposal(null);
          setError('This contact changed in Keap. Review the latest information before continuing.');
        }
      }
      // The type the server kept — the person's, the card's saved one, or the workspace default.
      const kept = { ...form, contactType: res.contactType !== undefined ? res.contactType : form.contactType };
      setForm(kept);
      // What was sent is now the server's; going Back from the confirm step is not "unsaved".
      setSavedForm(kept);
      setStep('confirm');
      return true;
    } catch (e) {
      const bad = invalidPhoneError(e);
      if (bad) setPhoneError(bad);
      else setError(messageFrom(e, 'The contact could not be checked against Keap.'));
      return false;
    }
  };

  /** The person's Contact Type change for the matched Keap contact, sent only by this approval. */
  const runTypeUpdate = async (): Promise<string | null> => {
    if (!captureId || !result || !typeProposal) return null;
    try {
      const r = await updateType({ externalContactId: typeProposal.ext, expected: typeProposal.from, contactType: typeProposal.to,
        requestId: typeProposal.requestId, source: { kind: 'card', captureId } }).unwrap();
      setTypeProposal(null);
      return r.status === 'checking'
        ? 'Keap did not confirm the Contact Type change yet. ContactPilot will check before doing anything else.'
        : 'Contact Type updated in Keap.';
    } catch (e) {
      const body = (e as { data?: { error?: string; message?: string } })?.data;
      setTypeProposal(null);
      throw new Error(body?.error === 'contact_type_changed'
        ? 'This contact changed in Keap. Review the latest Contact Type before updating.'
        : body?.message ?? 'We couldn’t update the Contact Type. Review the issue before trying again.');
    }
  };

  const onConfirm = async () => {
    if (!captureId || !result) return;
    setError(null);
    // Only a Contact Type change for someone already in Keap: just that, nothing else sent.
    if (result.proposedOperations.length === 0 && typeProposal) {
      try {
        const t = await runTypeUpdate();
        if (done) { done(t ?? 'Done.'); return; }
        setSaved({ externalContactId: existingId ?? null }); setStep('done');
      } catch (e) { setError((e as Error).message); }
      return;
    }
    try {
      const res = await confirm({ id: captureId, planHash: result.planHash }).unwrap();
      const t = typeProposal ? await runTypeUpdate().catch((e: Error) => e.message) : null;
      if (t && !t.startsWith('Contact Type updated')) { setError(t); return; }
      // Reopened from Review: the list closes this and says what happened.
      if (done) { done((res.executed ? 'Saved to your CRM.' : 'Approved. Nothing was sent to your CRM because CRM sending is switched off.') + (t ? ` ${t}` : '')); return; }
      setSaved({ externalContactId: res.externalContactId });
      setStep('done');
    } catch (e) {
      const data = (e as { data?: { error?: string; code?: string; message?: string } }).data;
      const kind = data?.code ?? data?.error ?? '';
      /*
       * The plan moved, or the look expired, while the details the person entered did not.
       * Fetch the plan as it is now and show it with what changed; nothing is approved
       * until they press Confirm again on the new one. Their corrections are kept.
       */
      if (REFRESHABLE.has(kind)) {
        const message = data?.message ?? 'This review is out of date. Please check the details again.';
        if (await reviewWith(form)) setError(message);
        return;
      }
      setError(data?.message ?? 'The contact could not be saved.');
      // Reviewed elsewhere, already used, or refused: back to the details to look again.
      if ((e as { status?: number }).status === 409) setStep('review');
    }
  };

  /** Discard, from Review: the card is cancelled and nothing is sent. */
  const onDiscard = async () => {
    if (!captureId) return;
    setError(null);
    try {
      await cancel(captureId).unwrap();
      if (done) { done('Discarded. Nothing was sent to your CRM.'); return; }
      setDiscarded(true);
    } catch (e) {
      setError(messageFrom(e, 'The card could not be discarded.'));
    }
  };

  const busy = uploadState.isLoading || extractState.isLoading;
  const resumeCapture = resumed.data?.capture ?? null;
  const canReadAgain = !resuming || resumeCapture?.hasImage === true;
  const edited = (name: Exclude<keyof FormState, 'tagIds' | 'phones'>, d?: DraftField) =>
    (form[name] || '') !== (d?.value ?? '');

  /*
   * Contact Type, once Keap has been checked: a new contact needs a person's choice (Keap
   * would otherwise apply its own default); an existing contact keeps the type Keap has.
   */
  const cardStatus: 'new' | 'existing' | 'other' = !result ? 'other'
    : result.proposedOperations.some((op) => op.intent === 'create_contact') ? 'new'
      : result.identity.externalContactIds.length > 0 ? 'existing' : 'other';
  // One matched Keap contact, with no doubt about who it is: only then can its type be changed.
  const existingId = cardStatus === 'existing' && result && !result.identity.requiresReview && result.identity.externalContactIds.length === 1
    ? result.identity.externalContactIds[0]! : null;
  const typeView = contactTypeView({
    types: result?.contactTypes, status: cardStatus, value: form.contactType,
    existingValue: result?.existingContactType ?? null,
  });
  const typeBlock = contactTypeBlocks([typeView]);
  const typeLabel = (v: string | null) => (v ? (result?.contactTypes ? labelOf(result.contactTypes, v) : v) : 'Not set');
  const typeChange = typeProposal
    ? `Update existing Keap contact: ${[form.givenName, form.familyName].filter(Boolean).join(' ') || 'this contact'} — Contact Type ${typeLabel(typeProposal.from)} → ${typeLabel(typeProposal.to)}`
    : null;
  return (
    <div className={`min-w-0 space-y-5 ${resuming ? '' : 'mx-auto max-w-3xl'}`}>
      {/* From Review the drawer is the frame; from Capture this is a page of its own. */}
      {resuming ? null : (
        <SectionHeading
          title="Business card"
          hint="Photograph a card, check what we read, then decide what goes into Keap."
        />
      )}

      {/* Where it came from: once — in the drawer's header when reopened from Review. */}
      {resuming || step !== 'capture' ? (
        <DrawerSource>
          <p className={resuming ? '' : 'text-sm font-medium text-slate-900'} data-captured-from="">
            Captured from Business Card
            {resumeCapture ? (
              <> · <time dateTime={resumeCapture.createdAt}>{formatWhen(resumeCapture.createdAt)}</time></>
            ) : null}
          </p>
        </DrawerSource>
      ) : null}

      {error ? <Alert tone="error">{error}</Alert> : null}

      {/* ── Reopened from Review: the states that have no form ───────────── */}
      {resuming && resumed.isLoading ? <Spinner label="Loading this card…" /> : null}
      {resuming && resumed.error ? <Alert tone="error">This card could not be loaded.</Alert> : null}
      {resuming && discarded ? (
        <Alert tone="info">Discarded. Nothing was sent to your CRM.</Alert>
      ) : null}
      {resuming && !discarded && resumeCapture?.status === 'cancelled' ? (
        <Alert tone="info">This card was discarded. Nothing was sent to your CRM.</Alert>
      ) : null}
      {resuming && !discarded && resumeCapture
        && (resumeCapture.status === 'uploaded' || resumeCapture.status === 'extracting') ? (
          <Alert tone="info">This card is still being read. Check back in a moment.</Alert>
        ) : null}
      {resuming && !discarded && resumeCapture && step === 'review' && !draft
        && !['uploaded', 'extracting', 'cancelled'].includes(resumeCapture.status) ? (
          <>
            <p className="text-sm text-slate-700">
              This card could not be read.
              {resumeCapture.hasImage ? '' : ' The photo has already been deleted, so it cannot be read again.'}
            </p>
            <DrawerFooter>
              <DrawerActions
                primary={resumeCapture.hasImage ? (
                  <Button onClick={onReExtract} disabled={extractState.isLoading}>
                    {extractState.isLoading ? 'Reading again…' : 'Try reading again'}
                  </Button>
                ) : undefined}
                secondary={[
                  <Button key="discard" variant="secondary" className={DANGER} onClick={() => void onDiscard()}>Discard</Button>,
                ]}
              />
            </DrawerFooter>
          </>
        ) : null}

      {/* ── Step 1: the photo ───────────────────────────────────────────── */}
      {step === 'capture' ? (
        <Card className="p-5">
          {previewUrl ? (
            <div className="space-y-4">
              <img
                src={previewUrl}
                alt="The business card you selected"
                className="mx-auto max-h-80 w-auto rounded-md border border-slate-200 object-contain"
              />
              <div className="flex flex-wrap gap-2">
                <Button onClick={onExtract} disabled={busy}>
                  {busy ? 'Reading the card…' : 'Read this card'}
                </Button>
                <Button variant="secondary" onClick={() => { setFile(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
                  Choose a different photo
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                The photo is sent for reading and then deleted. It is not kept.
              </p>
            </div>
          ) : (
            <div className="space-y-4 text-center">
              <p className="text-sm text-slate-600">Take a photo of the card, or choose one from this device.</p>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                <Button className="min-h-11" onClick={() => cameraRef.current?.click()}>
                  Take a photo
                </Button>
                <Button className="min-h-11" variant="secondary" onClick={() => galleryRef.current?.click()}>
                  Choose a file
                </Button>
              </div>

              {/*
                Two inputs, deliberately, with DIFFERENT accept values — each is what
                its platform needs, and neither is a filter we rely on. The server
                decides what is acceptable by reading the bytes.

                Camera: `accept="image/*"` with `capture="environment"`. The broad type
                is the part that matters: iOS Safari honours `capture` when the accept
                is `image/*`, and with a narrow MIME list it commonly ignores it and
                falls back to the ordinary picker — which is precisely the failure this
                control exists to avoid. `environment` asks for the rear camera, the
                one pointed at the card. A desktop browser ignores `capture` entirely
                and opens a file dialog.
              */}
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => chooseFile(e.currentTarget)}
              />

              {/*
                Library: the explicit list, because here it does real work. iPhone
                photos are HEIC, and Safari transcodes to JPEG on selection when the
                accept list does NOT include HEIC — so naming the three formats we
                support is what spares an iPhone user a rejection. Android and desktop
                pickers filter on it too.
              */}
              <input
                ref={galleryRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => chooseFile(e.currentTarget)}
              />
              <p className="text-xs text-slate-500">JPEG, PNG or WebP, up to 8 MB.</p>
            </div>
          )}
        </Card>
      ) : null}

      {/*
        Offered alongside the camera, not instead of it. Photographing one card and
        checking it carefully is a different act from clearing a pocketful after an
        event, and collapsing the two would make the careful case worse.
      */}
      {step === 'capture' && !file && !resuming ? <CardBatch onViewInReview={onViewInReview} /> : null}

      {/* ── Step 2: check the reading ───────────────────────────────────── */}
      {step === 'review' && draft && !discarded && resumeCapture?.status !== 'cancelled' ? (
        <div className="min-w-0 space-y-4">
          {/* Only what is about the card as a whole; a number's question is asked beside it. */}
          {cardLevelWarnings(draft.warnings).length > 0 ? (
            <Alert tone="info">
              <ul className="list-inside list-disc [overflow-wrap:anywhere]">
                {cardLevelWarnings(draft.warnings).map((w) => <li key={w}>{w}</li>)}
              </ul>
            </Alert>
          ) : null}

          {/* The photo beside the form only when there is one; otherwise the form takes the width. */}
          <div className={`grid min-w-0 gap-4 ${previewUrl ? 'md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]' : ''}`}>
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="The business card"
                className="w-full rounded-md border border-slate-200 object-contain md:sticky md:top-4"
              />
            ) : null}

            <DrawerSurface className="min-w-0 space-y-4">
              <section className="min-w-0 space-y-3" aria-label="Contact">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="First name" value={form.givenName} draft={draft.givenName}
                    edited={edited('givenName', draft.givenName)}
                    onChange={(v) => setForm({ ...form, givenName: v })} />
                  <Field label="Last name" value={form.familyName} draft={draft.familyName}
                    edited={edited('familyName', draft.familyName)}
                    onChange={(v) => setForm({ ...form, familyName: v })} />
                </div>
                <Field label="Job title" value={form.jobTitle} draft={draft.jobTitle}
                  edited={edited('jobTitle', draft.jobTitle)}
                  onChange={(v) => setForm({ ...form, jobTitle: v })} />
                <Field label="Company" value={form.company} draft={draft.company}
                  edited={edited('company', draft.company)}
                  onChange={(v) => setForm({ ...form, company: v })} />
                <Field label="Email" type="email" value={form.email} draft={draft.email}
                  edited={edited('email', draft.email)}
                  onChange={(v) => setForm({ ...form, email: v })} />
                <Field label="Website" value={form.website} draft={draft.website}
                  edited={edited('website', draft.website)}
                  onChange={(v) => setForm({ ...form, website: v })} />
              </section>

              <fieldset className="min-w-0 space-y-3 border-t border-slate-100 pt-4" data-address-editor="">
                <legend className="text-sm text-slate-700">Address</legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  {ADDRESS_FIELDS.map((a) => (
                    <label key={a.key} className={`block min-w-0 text-sm ${a.wide ? 'sm:col-span-2' : ''}`}>
                      <span className="text-slate-700">{a.label}</span>
                      <input
                        value={form.address[a.key]}
                        autoComplete={a.autoComplete}
                        onChange={(e) => setForm({ ...form, address: { ...form.address, [a.key]: e.target.value } })}
                        className="mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 px-3 py-2 text-base sm:text-sm"
                      />
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="border-t border-slate-100 pt-4">
                <PhoneListEditor
                  rows={form.phones}
                  onChange={(phones) => { setForm({ ...form, phones }); setPhoneError(null); }}
                  deliveries={result?.phones}
                  error={phoneError}
                />
              </div>

              {/*
                One CRM Tags section. Where the contact came from is context, said once
                above; it is recorded by the capture, never added as a tag.
              */}
              <div className="border-t border-slate-100 pt-4">
                <CrmTagPicker
                  vocab={vocab}
                  selected={form.tagIds}
                  onChange={(ids) => setForm({ ...form, tagIds: ids })}
                  legend="CRM Tags"
                  empty="No CRM tags. Add one if you know who this is to you."
                  onManageTags={onOpenCrm}
                />
              </div>
            </DrawerSurface>
          </div>

          <DrawerFooter>
            <DrawerActions
              primary={(
                <Button onClick={onContinue} disabled={reviewState.isLoading || Boolean(blocked)}>
                  {reviewState.isLoading ? 'Checking your CRM…' : 'Continue'}
                </Button>
              )}
              secondary={[
                canReadAgain ? (
                  <Button key="again" variant="secondary" onClick={onReExtract} disabled={extractState.isLoading}>
                    {extractState.isLoading ? 'Reading again…' : resuming ? 'Try reading again' : 'Read the card again'}
                  </Button>
                ) : null,
                resuming ? (
                  <Button key="discard" variant="secondary" className={DANGER} onClick={() => void onDiscard()}>Discard</Button>
                ) : (
                  <Button key="over" variant="secondary" onClick={reset}>Start over</Button>
                ),
              ]}
            >
              {blocked ? <p className="text-xs text-amber-900 [overflow-wrap:anywhere]" data-blocked-note="">{blocked}</p> : null}
              {phoneError ? <p className="text-xs text-red-700">Fix the phone numbers above to continue.</p> : null}
            </DrawerActions>
          </DrawerFooter>
        </div>
      ) : null}

      {/* ── Step 3: approve what happens ────────────────────────────────── */}
      {step === 'confirm' && result ? (
        <DrawerSurface padded="px-5" className="divide-y divide-slate-200">
          <div className="py-4">
            <h3 className="text-base font-semibold text-slate-900">Review before saving to Keap</h3>
            <p className="mt-1 text-sm text-slate-500">Nothing has been sent yet.</p>
          </div>

          <div className="py-4">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Identity</h4>
            <p className="mt-1 text-sm text-slate-800">
              {MATCH_COPY[result.identity.matchTier] ?? result.identity.matchTier}
            </p>
          </div>

          {result.sharedAddress ? (
            <div className="py-4" data-card-shared="">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Shared email</h4>
              <SharedAddressConfirm
                name={[form.givenName, form.familyName].filter(Boolean).join(' ')}
                email={form.email}
                state={result.sharedAddress}
                editable={!reviewState.isLoading && !confirmState.isLoading}
                busy={reviewState.isLoading}
                onConfirm={() => { void reviewWith(form, { confirmSharedAddress: true }); }}
                onSkip={() => { void reviewWith(form, { confirmSharedAddress: false }); }}
                onChoose={(keapContactId) => { void reviewWith(form, { keapContactId }); }}
                idBase={`card-${captureId ?? 'new'}`}
              />
            </div>
          ) : null}

          {typeView.kind !== 'hidden' ? (
            <div className="py-4" data-card-contact-type="">
              <ContactTypeField
                types={result.contactTypes}
                status={cardStatus}
                value={form.contactType}
                existingValue={result.existingContactType ?? null}
                disabled={reviewState.isLoading || confirmState.isLoading}
                onChange={(contactType) => { void reviewWith({ ...form, contactType, contactTypeChosen: true }); }}
                idBase={`card-${captureId ?? 'new'}`}
                {...(existingId ? {
                  keap: { status: 'ok' as const, contactType: result.existingContactType ?? null },
                  proposed: typeProposal?.to ?? null,
                  onPropose: (to: string | null) => setTypeProposal(to
                    ? { ext: existingId, from: result.existingContactType ?? null, to, requestId: typeProposal?.requestId ?? crypto.randomUUID() }
                    : null),
                } : {})}
              />
            </div>
          ) : null}

          <div className="py-4" data-card-action="">
            {typeChange ? (
              <p className="mb-3 text-sm font-medium text-slate-900 [overflow-wrap:anywhere]" data-type-change="">{typeChange}</p>
            ) : null}
            {result.proposedOperations.length === 0 ? (typeChange ? null : (
              <p className="text-sm text-slate-600">
                {result.sharedAddress && !result.sharedAddress.confirmed
                  ? 'Nothing will be sent until you confirm the person who uses this email.'
                  : result.sharedAddress?.match === 'in_use'
                    ? 'Nothing will be sent until you choose the correct Keap contact.'
                    : 'Nothing to do for this card.'}
              </p>
            )) : (
              // The server's plan, as values: exactly what Confirm will send.
              <PlanSummary operations={result.proposedOperations} tagName={tagName} typeLabel={(v) => typeLabel(v)} />
            )}
          </div>

          {/* Numbers that will NOT simply be sent, and why. The ones that will are listed above. */}
          {result.phones && result.phones.some((p) => p.delivery !== 'will_send') ? (
            <div className="py-4" data-phone-delivery="">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone numbers not sent</h4>
              <ul className="mt-1 space-y-1 text-sm text-slate-800">
                {result.phones.filter((p) => p.delivery !== 'will_send').map((p) => (
                  <li key={`${p.type}-${p.value}`} className="min-w-0 [overflow-wrap:anywhere]">
                    <span className="text-slate-500">{PHONE_TYPE_LABEL[p.type] ?? 'Other'}</span> {p.value}
                    <span className="block text-xs text-slate-600">{DELIVERY_TEXT[p.delivery as Exclude<typeof p.delivery, 'will_send'>]}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.missingTagMappings.length > 0 ? (
            <div className="py-4">
              <Alert tone="error">
                <p>
                  {result.missingTagMappings.map(tagName).join(' and ')} {result.missingTagMappings.length > 1 ? 'have' : 'has'} no
                  CRM tag chosen yet, so this cannot be saved.
                </p>
                <button type="button" onClick={onOpenCrm} className="mt-1 inline-flex min-h-11 items-center font-medium underline">
                  Choose CRM tags
                </button>
              </Alert>
            </div>
          ) : null}

          {typeBlock ? (
            <div className="py-4"><p className="text-sm text-amber-900" data-blocked-note="">{typeBlock}</p></div>
          ) : null}

          {(() => {
            // What saving spends from today's CRM changes, before anyone presses Confirm.
            const u = result.crmWriteEnabled && result.proposedOperations.length > 0
              ? approvalUsageText(result.usage) : null;
            return u ? (
              <div className="py-4" data-approval-usage="">
                {u.tone === 'error' ? <Alert tone="error">{u.text}</Alert>
                  : <p className="text-sm text-slate-700">{u.text}</p>}
              </div>
            ) : null;
          })()}

          {!result.crmWriteEnabled ? (
            <div className="py-4">
              <Alert tone="info">
                Sending to your CRM is switched off. You can walk through the
                confirmation, and nothing will be sent.
              </Alert>
            </div>
          ) : null}

          <DrawerFooter inlineClassName="py-4">
            <DrawerActions
              primary={(
                <Button
                  onClick={onConfirm}
                  disabled={
                    confirmState.isLoading
                    || updateTypeState.isLoading
                    || reviewState.isLoading
                    || (result.proposedOperations.length === 0 && !typeProposal)
                    || result.missingTagMappings.length > 0
                    || Boolean(typeBlock)
                  }
                >
                  {confirmState.isLoading || updateTypeState.isLoading ? 'Saving…'
                    : result.proposedOperations.length === 0 && typeProposal ? 'Approve change' : 'Confirm and save to Keap'}
                </Button>
              )}
              secondary={[
                <Button key="back" variant="secondary" onClick={() => setStep('review')}>Back</Button>,
                resuming ? null : <Button key="cancel" variant="secondary" onClick={reset}>Cancel</Button>,
              ]}
            />
          </DrawerFooter>
        </DrawerSurface>
      ) : null}

      {/* ── Step 4: what happened ───────────────────────────────────────── */}
      {step === 'done' && saved ? (
        <DrawerSurface className="space-y-3">
          <Alert tone="success">Saved to Keap.</Alert>
          {resuming ? null : <Button onClick={reset}>Capture another card</Button>}
        </DrawerSurface>
      ) : null}

      {busy && step === 'capture' ? <Spinner label="Reading the card…" /> : null}
    </div>
  );
}
